import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, type PostValue } from './client'

/**
 * Settings -> Customers, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Customers.php`:
 *
 *   getCustomersData         -> GET  /settings/customers/companies
 *   getGroupsData            -> GET  /settings/customers/groups
 *   groupDetails/groupSettings -> GET /settings/customers/groups/{id}
 *   saveGroup / updateDetails  -> POST /settings/customers/groups
 *   updateCompanies          -> POST /settings/customers/groups/{id}/companies
 *   addCompanyForm           -> GET  /settings/customers/companies/new
 *   createCompany            -> POST /settings/customers/companies
 *   company_* (filele fisei) -> GET  /settings/customers/companies/{id}/<fila>
 *   update* (scrierile)      -> POST /settings/customers/companies/{id}/<fila>
 *   officeIps / officeNetworks -> /settings/customers/offices/{office}/{ips,networks}
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * O salvare refuzata vine ca 400 cu mesajul serverului, deci ajunge la ecran
 * ca `ApiError` - nu ca un `echo 'An error occured!'` in corpul unui 200, cum
 * raspundea portalul.
 */

/** O fila din bara. `group` desparte "Customers" de "Support Licenses". */
export type CustomersTab = {
  id: string
  group: string
  label: string
  icon: string
  right: string
  href: string
}

export type CustomersPanelTab = { id: string; label: string }

export type CustomersShell = {
  pageTitle: string
  moduleUrl: string
  /** DEJA filtrate pe server: aici vin numai filele pe care omul are voie sa le vada. */
  tabs: CustomersTab[]
  panelTabs: CustomersPanelTab[]
  ready: boolean
  canCreate: boolean
  canEdit: boolean
  canEnable: boolean
}

export const TABS = ['companies', 'groups'] as const

export type CustomersTabId = (typeof TABS)[number]

export function isCustomersTab(value: string): value is CustomersTabId {
  return (TABS as readonly string[]).includes(value)
}

/** Raspunsul unei liste, in forma pe care o astepta DataTables. */
export type TablePage<T> = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

export type TableRequest = {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

export function tableParams(r: TableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

/** Un rand din lista de firme (`CustomerCompanies.companiesModel`). */
export type CompanyRow = {
  ID: number
  Nume: string
  /** `firma.Deleted`: 1 = ACTIVA. Numele coloanei minte, valoarea nu. */
  Status: number
  Devices: number
  Users: number
  Offices: number
  Departments: number
  itManager: string | null
  SystemAdmin: string | null
  agentVersion: string
  inSelector: boolean
  whyNot: string
  canEnable: boolean
  DT_RowId?: string
}

export type GroupRow = {
  ID: number
  group_name: string
  companies: number
  createdBy: string
  date: string
  DT_RowId?: string
}

export type GroupCompany = { ID: number; Nume: string; selected: number }

export type GroupPage = {
  group: { id: number; name: string }
  companies: GroupCompany[]
  canWrite: boolean
}

export type NamedId = { ID: number; name: string }

/**
 * Un modul din lista oferita. Eventee si Easeetask nu vin deloc
 * (`ModuleCatalog::REMOVED`); `default` = bifat de la inceput pe o firma noua.
 */
export type ModuleRow = { id: number; name: string; slug?: string; default?: boolean }

export type Timezone = { id: number; timezone: string }

/** `siemGroups`: grupurile SIEM ale furnizorului (sau ale platformei), fara nimic ales dinainte. */
export type NewCompanyForm = { itManagers: NamedId[]; modules: ModuleRow[]; siemGroups?: { ID: number; name: string }[] }

export type CompanyEventDay = { day: string; rows: { time: string; details: string }[] }

export type Department = { ID: number; Nume: string }

export type Office = { ID: number; Name: string }

export type OfficeAdmin = { admin_id: number; ticket_responsible: number }

export type OfficePage = {
  office: { id: number; name: string; latitude: string; longitude: string; mainOffice: number }
  admins: OfficeAdmin[]
  administrators: NamedId[]
  officeDepartments: Department[]
  departments: Department[]
  canEdit: boolean
}

export type CompanySettingsPayload = {
  company: {
    id: number
    name: string
    contactPerson: string
    itManager: number
    language: string
    timezone: number
    uniqueRegistration: string
    taxIdentification: string
    latitude: string
    longitude: string
    workingProgram: string
    premium: number
    disableRemoteConsent: number
    alwaysDisableConsent: number
    enableReporting: number
    externalProvider: number
    enabled: boolean
    twoFactor: number
    /** Sigla din capul filei Settings (`get_company_logo`); gol fara server de descarcari. */
    logoUrl: string
  }
  itManagers: NamedId[]
  timezones: Timezone[]
  domains: string[]
  cnrs: { id: number; name: string; top: number }[]
  vips: { id: number; name: string }[]
  /** Furnizorul 59 nu are voie la consimtamantul de la distanta - ca in portal. */
  consentLocked: boolean
  /** Grupul SIEM al firmei; null inainte de migrarea grupurilor. */
  siem?: CompanySiemCard | null
}

export type CompanySiemCard = {
  /** null = fara grup: evenimentele nu pleaca, XDR nu porneste */
  group: { ID: number; name: string } | null
  /** grupul vine de la firma-parinte */
  inherited: boolean
  /** numele firmei-parinte; null = firma de varf */
  parent: string | null
  /** administratorul de platforma, la o firma de varf */
  canChange: boolean
  groups: { ID: number; name: string }[]
}

export type Person = { id: number; text: string }

export type OfficeIp = { ID: number; ip: string }

export type OfficeNetwork = { ID: number; cidr: string }

export type BillingForm = {
  nrc: string
  fiscal_code: string
  iban: string
  bank: string
  county: string
  contact_email: string
  company_address: string
}

const KEY = ['settings', 'customers'] as const

export const customersShellQuery = queryOptions({
  queryKey: [...KEY, 'shell'],
  queryFn: () => apiGet<CustomersShell>('/settings/customers'),
})

export function companiesQuery(r: TableRequest) {
  return queryOptions({
    queryKey: [...KEY, 'companies', r],
    queryFn: () => apiGet<TablePage<CompanyRow> & { ready: boolean }>(`/settings/customers/companies?${tableParams(r).toString()}`),
  })
}

export function groupsQuery(r: TableRequest) {
  return queryOptions({
    queryKey: [...KEY, 'groups', r],
    queryFn: () => apiGet<TablePage<GroupRow>>(`/settings/customers/groups?${tableParams(r).toString()}`),
  })
}

export function groupQuery(groupId: number) {
  return queryOptions({
    queryKey: [...KEY, 'group', groupId],
    queryFn: () => apiGet<GroupPage>(`/settings/customers/groups/${groupId}`),
    enabled: groupId > 0,
  })
}

/** Fara `id` creeaza, cu `id` redenumeste - `saveGroup` / `updateDetails`. */
export function saveGroup(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/customers/groups', fields)
}

export function saveGroupCompanies(groupId: number, companies: number[]) {
  return apiPost<{ saved: boolean }>(`/settings/customers/groups/${groupId}/companies`, { companies })
}

export const newCompanyQuery = queryOptions({
  queryKey: [...KEY, 'company-form'],
  queryFn: () => apiGet<NewCompanyForm>('/settings/customers/companies/new'),
})

export function createCompany(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>('/settings/customers/companies', fields)
}

export function companyEventsQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'events', companyId],
    queryFn: () => apiGet<{ events: CompanyEventDay[] }>(`/settings/customers/companies/${companyId}/events`),
    enabled: companyId > 0,
  })
}

export function departmentsQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'departments', companyId],
    queryFn: () => apiGet<{ departments: Department[] }>(`/settings/customers/companies/${companyId}/departments`),
    enabled: companyId > 0,
  })
}

export function departmentQuery(companyId: number, departmentId: number) {
  return queryOptions({
    queryKey: [...KEY, 'department', companyId, departmentId],
    queryFn: () =>
      apiGet<{ department: { id: number; name: string; email: string } }>(
        `/settings/customers/companies/${companyId}/departments/${departmentId}`,
      ),
    enabled: companyId > 0 && departmentId > 0,
  })
}

export function saveDepartment(companyId: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>(`/settings/customers/companies/${companyId}/departments`, fields)
}

export function removeDepartment(companyId: number, departmentId: number) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/departments/${departmentId}/remove`, {})
}

export function officesQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'offices', companyId],
    queryFn: () => apiGet<{ offices: Office[] }>(`/settings/customers/companies/${companyId}/offices`),
    enabled: companyId > 0,
  })
}

export function newOfficeQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'office-form', companyId],
    queryFn: () =>
      apiGet<{ administrators: NamedId[]; departments: Department[] }>(
        `/settings/customers/companies/${companyId}/offices/new`,
      ),
    enabled: companyId > 0,
  })
}

export function officeQuery(companyId: number, officeId: number) {
  return queryOptions({
    queryKey: [...KEY, 'office', companyId, officeId],
    queryFn: () => apiGet<OfficePage>(`/settings/customers/companies/${companyId}/offices/${officeId}`),
    enabled: companyId > 0 && officeId > 0,
  })
}

export function saveOffice(companyId: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>(`/settings/customers/companies/${companyId}/offices`, fields)
}

export function removeOffice(companyId: number, officeId: number) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/offices/${officeId}/remove`, {})
}

export function saveOfficeAdmins(companyId: number, officeId: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/offices/${officeId}/admins`, fields)
}

export function saveOfficeDepartments(companyId: number, officeId: number, departments: number[]) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/offices/${officeId}/departments`, {
    departments,
  })
}

export function officeIpsQuery(officeId: number) {
  return queryOptions({
    queryKey: [...KEY, 'office-ips', officeId],
    queryFn: () => apiGet<{ ips: OfficeIp[]; canWrite: boolean }>(`/settings/customers/offices/${officeId}/ips`),
    enabled: officeId > 0,
  })
}

export function saveOfficeIp(officeId: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; message?: string }>(`/settings/customers/offices/${officeId}/ips`, fields)
}

export function officeNetworksQuery(officeId: number) {
  return queryOptions({
    queryKey: [...KEY, 'office-networks', officeId],
    queryFn: () =>
      apiGet<{ networks: OfficeNetwork[]; tableHere: boolean; canWrite: boolean }>(
        `/settings/customers/offices/${officeId}/networks`,
      ),
    enabled: officeId > 0,
  })
}

export function saveOfficeNetwork(officeId: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; message?: string }>(`/settings/customers/offices/${officeId}/networks`, fields)
}

/**
 * "Create offices automatically for unknown networks" - setarea firmei din
 * `it_office_auto_create`, citita de ph4.api la raportarea agentului.
 */
export type OfficeAutoCreate = { enabled: boolean; tableHere: boolean; canWrite: boolean }

export function officeAutoCreateQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'office-auto-create', companyId],
    queryFn: () => apiGet<OfficeAutoCreate>(`/settings/customers/companies/${companyId}/office_auto_create`),
    enabled: companyId > 0,
  })
}

export function saveOfficeAutoCreate(companyId: number, enabled: boolean) {
  return apiPost<{ saved: boolean; enabled: boolean }>(`/settings/customers/companies/${companyId}/office_auto_create`, {
    enabled: enabled ? '1' : '0',
  })
}

export function companySettingsQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'company-settings', companyId],
    queryFn: () => apiGet<CompanySettingsPayload>(`/settings/customers/companies/${companyId}/settings`),
    enabled: companyId > 0,
  })
}

export function saveCompanySettings(companyId: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/settings`, fields)
}

export function saveDomains(companyId: number, domains: string[]) {
  return apiPost<{ saved: boolean; domains: string[] }>(`/settings/customers/companies/${companyId}/domains`, {
    domains,
  })
}

export function saveCnrs(companyId: number, cnr: number[], top: number) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/cnrs`, { cnr, cnr_top: top })
}

export function saveVips(companyId: number, vip: number[]) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/vips`, { vip })
}

export function statusWarningQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'status', companyId],
    queryFn: () => apiGet<{ devices: number }>(`/settings/customers/companies/${companyId}/status`),
    enabled: companyId > 0,
  })
}

export function setCompanyStatus(companyId: number, status: number) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/status`, { status })
}

export function peopleQuery(companyId: number, search: string) {
  return queryOptions({
    queryKey: [...KEY, 'people', companyId, search],
    queryFn: () =>
      apiGet<{ people: Person[] }>(
        `/settings/customers/companies/${companyId}/people?q=${encodeURIComponent(search)}`,
      ),
    enabled: companyId > 0,
  })
}

export function companyModulesQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'modules', companyId],
    queryFn: () =>
      apiGet<{ modules: ModuleRow[]; assigned: number[] }>(`/settings/customers/companies/${companyId}/modules`),
    enabled: companyId > 0,
  })
}

export function saveCompanyModules(companyId: number, modules: number[]) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/modules`, { modules })
}

export function agentSettingsQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'agent', companyId],
    queryFn: () => apiGet<{ mainColor: string }>(`/settings/customers/companies/${companyId}/agent_settings`),
    enabled: companyId > 0,
  })
}

export function saveAgentSettings(companyId: number, mainColor: string) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/agent_settings`, {
    'settings[main_color]': mainColor,
  })
}

export function billingQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'billing', companyId],
    queryFn: () => apiGet<{ billing: BillingForm }>(`/settings/customers/companies/${companyId}/billing`),
    enabled: companyId > 0,
  })
}

export function saveBilling(companyId: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/billing`, fields)
}

export function twoFactorQuery(companyId: number) {
  return queryOptions({
    queryKey: [...KEY, 'two-factor', companyId],
    queryFn: () => apiGet<{ enabled: boolean; ips: string[] }>(`/settings/customers/companies/${companyId}/two_factor`),
    enabled: companyId > 0,
  })
}

export function saveTwoFactorIps(companyId: number, ips: string[]) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/two_factor`, { ips })
}

export function setTwoFactorStatus(companyId: number, on: boolean) {
  return apiPost<{ saved: boolean }>(`/settings/customers/companies/${companyId}/two_factor`, { status: on ? 1 : 0 })
}

/**
 * "Change Logo" din fila Settings a fisei clientului -
 * `Customers::changeCompanyLogo` + `Companies::company_logo` cu `company` in
 * POST. `multipart/form-data`, fisierul in `company_logo` ca in `change_logo.php`.
 */
export function uploadCustomerLogo(companyId: number, file: File) {
  const body = new FormData()
  body.append('company_logo', file, file.name)
  return apiPostForm<{ saved: boolean; url: string }>(`/settings/customers/companies/${companyId}/logo`, body)
}
